import {useEffect, useMemo, useRef, useState} from 'react';
import {useDebounce} from 'react-use';
import {Popover, TextField} from '@ezcater/tapas';
import {useRouter} from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import {MIN_CHARS} from '@/components/global/layout/AppBar/SearchBar/constants';
import {type PartialTypeaheadResult} from '@/components/global/layout/AppBar/SearchBar/types';
import {useTypeaheadData} from '@/components/global/layout/AppBar/SearchBar/useTypeaheadData';
import EventBarSearchHistoryOption from '../EventBarSearchHistoryOption';
import {useEventBarV2} from '../EventBarV2Provider';
import useApplySearchHistoryEntry from '../hooks/useApplySearchHistoryEntry';
import useEventBarDraftOrders, {type EventBarDraftOrder} from '../hooks/useEventBarDraftOrders';
import useEventBarSearchHistory, {
  type EventBarSearchHistoryEntry,
} from '../hooks/useEventBarSearchHistory';
import useEventBarTypeaheadAddress from '../hooks/useEventBarTypeaheadAddress';
import {type SubmitSearchOverride} from '../hooks/useEventBarV2Search';
import {useEventBarV2Tracking} from '../hooks/useEventBarV2Tracking';
import useSyncWhatWithFilters from '../hooks/useSyncWhatWithFilters';
import InfoBanner from '../InfoBanner';
import {type AdvanceDecision} from '../utils/askFlow';
import {focusAdjacentTabbable} from '../utils/fields';
import {typeaheadResultToStagedFilter, WHAT_SUGGESTIONS} from '../utils/what';
import {mergeRecentItems} from './eventBarRecentItems';
import SuggestedSearchChip from './SuggestedSearchChip';
import WhatDraftOrderOption from './WhatDraftOrderOption';
import WhatTypeaheadOption from './WhatTypeaheadOption';

type WhatProps = {
  isCompressed?: boolean;
  isOpen: boolean;
  // Hands the bar on to the next field that still needs asking about. Reports what it did, so a
  // decision other than `open` — nothing left to ask, or a menu page — runs the search instead.
  onAdvance: () => AdvanceDecision;
  // Runs the bar's search (Where check, commit, then search) with What's own keyword or filter.
  onSearch: (override?: SubmitSearchOverride) => Promise<void>;
  setIsOpen: (open: boolean) => void;
};

const MAX_CHARS = 500;

const chipOptionId = (index: number, idSuffix: string) =>
  `what-option-${WHAT_SUGGESTIONS[index].key}-${idSuffix}`;

type PopoverOpenChange = NonNullable<React.ComponentProps<typeof Popover.Root>['onOpenChange']>;

const What: React.FC<WhatProps> = ({
  isCompressed = false,
  isOpen,
  onAdvance,
  onSearch,
  setIsOpen,
}) => {
  const {t} = useTranslation('app-bar');
  const {hasUserEditedWhatRef, whatValue, setWhatValue, setWhatSelection, setStagedSearchFilter} =
    useEventBarV2();
  const router = useRouter();
  const draftOrders = useEventBarDraftOrders();
  const {
    trackDraftOrderSelected,
    trackSearchHistorySelected,
    trackRecommendedSearch,
    trackTypeaheadClick,
    trackTypeaheadViewed,
  } = useEventBarV2Tracking();
  const searchHistory = useEventBarSearchHistory();
  const applySearchHistoryEntry = useApplySearchHistoryEntry();

  const [activeIndex, setActiveIndex] = useState(-1);
  const [debouncedWhatValue, setDebouncedWhatValue] = useState(whatValue);

  const inputRef = useRef<HTMLInputElement>(null);
  const selectOnMouseUpRef = useRef(false);
  const anchorRef = useRef<HTMLLabelElement>(null);

  const idSuffix = isCompressed ? 'compressed' : 'expanded';
  const containerId = `what-container-${idSuffix}`;
  const inputId = `what-input-${idSuffix}`;
  const listboxId = `what-suggestions-listbox-${idSuffix}`;

  const overrideAddress = useEventBarTypeaheadAddress(isOpen);

  useDebounce(() => setDebouncedWhatValue(whatValue), 200, [whatValue]);

  const {caterers, cuisines, diversity, flattenedSortedResults} = useTypeaheadData(
    debouncedWhatValue,
    isOpen,
    overrideAddress,
  );

  const hasResults = caterers.length + cuisines.length + diversity.length > 0;
  const showResults = whatValue.length >= MIN_CHARS && hasResults;

  // Visual-order flat list (caterers → cuisines → businesses) for arrow-key nav
  // and aria-activedescendant, matching the old event bar's typeahead order.
  const resultOptions = useMemo(
    () => (showResults ? [...caterers, ...cuisines, ...diversity] : []),
    [showResults, caterers, cuisines, diversity],
  );

  // Fire the "viewed typeahead" event ~300ms after a response settles while the panel is open,
  // mirroring SearchBar's debounced view tracking. Keyed on flattenedSortedResults (undefined until
  // a response settles), so it fires for every completed response — including zero results — matching
  // V1 and the viewed-typeahead-suggestion contract (SEARCH-2368), not only when results are visible.
  useDebounce(
    () => {
      if (!isOpen || flattenedSortedResults === undefined) return;
      trackTypeaheadViewed({
        caterers,
        cuisines,
        diversity,
        keyword: whatValue,
        suggestionCount: flattenedSortedResults.length,
      });
    },
    300,
    [flattenedSortedResults],
  );

  const cuisineStart = caterers.length;
  const diversityStart = caterers.length + cuisines.length;

  const resultOptionId = (index: number) => `what-result-${idSuffix}-${index}`;
  const recentOptionId = (index: number) => `what-recent-${idSuffix}-${index}`;

  // Draft orders and search history blended into one recency-ordered list, deduped by fulfillment
  // detail and capped — neither source is prioritized, the most recent wins. See mergeRecentItems.
  const recentItems = useMemo(
    () => mergeRecentItems(draftOrders, searchHistory),
    [draftOrders, searchHistory],
  );

  const showRecent = !showResults && recentItems.length > 0;

  const activeListLength = showResults
    ? resultOptions.length
    : recentItems.length + WHAT_SUGGESTIONS.length;

  const activeDescendantId =
    activeIndex < 0
      ? undefined
      : showResults
        ? resultOptionId(activeIndex)
        : activeIndex < recentItems.length
          ? recentOptionId(activeIndex)
          : chipOptionId(activeIndex - recentItems.length, idSuffix);

  useSyncWhatWithFilters();

  // Reset the active option whenever the list it indexes into changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveIndex(-1);
  }, [debouncedWhatValue, showResults]);

  const handleOpenChange: PopoverOpenChange = (next, eventDetails) => {
    // Presses inside the anchor container are part of the field itself, so they must not dismiss
    // the popup. Base UI replaces Radix's `onInteractOutside` with a cancelable `onOpenChange`.
    if (eventDetails.reason === 'outside-press') {
      const target = eventDetails.event.target as HTMLElement | null;
      if (target?.closest(`#${containerId}`)) {
        eventDetails.cancel();
        return;
      }
    }

    setIsOpen(next);
    if (!next) setActiveIndex(-1);
  };

  const handleSelect = (suggestion: (typeof WHAT_SUGGESTIONS)[number]) => {
    const label = t(`eventBarV2.what.suggestions.${suggestion.key}`);
    trackRecommendedSearch(label, suggestion.filter.source);
    setStagedSearchFilter(suggestion.filter);
    setWhatValue(label);
    hasUserEditedWhatRef.current = true;
    setIsOpen(false);
    // Nothing left to ask about (or a menu page, which edits in place) — search on the spot.
    if (onAdvance().kind !== 'open') onSearch({stagedFilter: suggestion.filter});
  };

  const handleSelectResult = (result: PartialTypeaheadResult) => {
    const rank = resultOptions.findIndex(option => option === result);
    trackTypeaheadClick({
      suggestion: result,
      rank: rank >= 0 ? rank + 1 : null,
      keyword: whatValue,
      suggestionCount: resultOptions.length,
    });
    // A cuisine/diversity result carries a structured filter so we run a filtered search.
    const stagedFilter = typeaheadResultToStagedFilter(result);
    setWhatValue(result.display);
    setWhatSelection(stagedFilter ? null : result);
    setStagedSearchFilter(stagedFilter);
    hasUserEditedWhatRef.current = true;
    setIsOpen(false);
    if (onAdvance().kind !== 'open') {
      onSearch(stagedFilter ? {stagedFilter} : {keyword: result.display, stagedFilter: null});
    }
  };

  const handleSelectDraftOrder = (draftOrder: EventBarDraftOrder) => {
    trackDraftOrderSelected(draftOrder.catererId, 'what');
    setIsOpen(false);
    router.push(draftOrder.resumeHref);
  };

  const handleSelectHistory = (entry: EventBarSearchHistoryEntry) => {
    // Rank/count over the history cells actually shown. Filter out the draft order cells.
    // The metric means the same thing as in the Where panel.
    const historyEntries = recentItems.flatMap(item =>
      item.type === 'history' ? [item.entry] : [],
    );
    const rank = historyEntries.findIndex(
      historyEntry => historyEntry.fulfillmentDetailId === entry.fulfillmentDetailId,
    );
    trackSearchHistorySelected({
      entry,
      rank: rank + 1,
      resultCount: historyEntries.length,
      source: 'what',
    });
    setIsOpen(false);
    void applySearchHistoryEntry(entry);
  };

  const activateActiveOption = () => {
    if (activeIndex < 0) return;
    if (showResults) {
      const result = resultOptions[activeIndex];
      if (result) handleSelectResult(result);
    } else if (activeIndex < recentItems.length) {
      const item = recentItems[activeIndex];
      if (item.type === 'draft') handleSelectDraftOrder(item.draftOrder);
      else handleSelectHistory(item.entry);
    } else {
      const suggestion = WHAT_SUGGESTIONS[activeIndex - recentItems.length];
      if (suggestion) handleSelect(suggestion);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const lastIndex = activeListLength - 1;
    switch (event.key) {
      case 'Tab':
        // While the panel is open, Base UI's popup focus guards have no trigger to hand focus back
        // to, so Tab would dead-end inside the portal. Move to the next field ourselves — clearing
        // the active option too, since closing through `setIsOpen` skips `onOpenChange`. Only
        // suppress the keystroke if focus actually moved, so a field with no adjacent tabbable
        // falls through to the browser's own Tab rather than stranding focus on a closed panel.
        if (isOpen) {
          setIsOpen(false);
          setActiveIndex(-1);
          if (focusAdjacentTabbable(event.currentTarget, event.shiftKey ? -1 : 1)) {
            event.preventDefault();
          }
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        setIsOpen(true);
        setActiveIndex(prev => (prev >= lastIndex ? 0 : prev + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setIsOpen(true);
        setActiveIndex(prev => (prev <= 0 ? lastIndex : prev - 1));
        break;
      case 'Home':
        if (isOpen) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          event.preventDefault();
          setActiveIndex(lastIndex);
        }
        break;
      case 'Enter':
        if (isOpen && activeIndex >= 0) {
          event.preventDefault();
          activateActiveOption();
        }

        // Enter with nothing highlighted: continue the search flow at the next field that still
        // needs asking about, or just run the keyword search when none does.
        if (activeIndex === -1) {
          setIsOpen(false);
          if (onAdvance().kind !== 'open') onSearch();
        }
        break;
      default:
        break;
    }
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <label
        className={twJoin(
          'relative flex flex-col justify-center rounded-full border-2 border-transparent py-2 transition-all duration-200 focus-within:z-10 hover:z-10 motion-reduce:transition-none',
          isCompressed ? 'h-[60px] px-5' : 'h-20 px-8',
        )}
        htmlFor={inputId}
        id={containerId}
        ref={anchorRef}
      >
        {!isCompressed && (
          <span className="text-nowrap font-semibold text-peppercorn-800">
            {t('eventBarV2.what.label')}
          </span>
        )}
        <TextField.Root className="border-none bg-transparent p-0 focus-within:ring-0 hover:bg-transparent">
          <TextField.Input
            aria-activedescendant={activeDescendantId}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={isOpen}
            autoComplete="off"
            className={twJoin(
              'truncate p-0 focus:outline-none focus:ring-0',
              isCompressed && 'placeholder:text-peppercorn-500',
            )}
            data-testid="what-input"
            id={inputId}
            onClick={() => setIsOpen(true)}
            onFocus={event => {
              const inputField = event.currentTarget;

              inputField.setSelectionRange(0, inputField.value.length, 'backward');
              selectOnMouseUpRef.current = true;
              setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            onMouseUp={event => {
              // Safari/Firefox collapse the focus-triggered select() on mouseup; guard the first one.
              if (selectOnMouseUpRef.current) event.preventDefault();
              selectOnMouseUpRef.current = false;
            }}
            ref={inputRef}
            placeholder={t('eventBarV2.what.placeholder')}
            role="combobox"
            onChange={event => {
              setWhatValue(event.target.value);
              setWhatSelection(null);
              hasUserEditedWhatRef.current = true;
              // Typing is a keyword search, so drop any filter staged from a suggestion chip.
              setStagedSearchFilter(null);
              setIsOpen(true);
            }}
            maxLength={MAX_CHARS}
            value={whatValue}
          />
        </TextField.Root>
      </label>
      <Popover.Content
        align="start"
        anchor={anchorRef}
        className="mt-2 max-h-[min(600px,var(--available-height))] w-[460px] overflow-y-auto rounded-2xl border border-peppercorn-100 bg-white p-4 shadow-lg"
        data-event-bar-surface
        data-testid="what-suggestions-panel"
        finalFocus={false}
        initialFocus={false}
        sideOffset={8}
      >
        {showResults ? (
          // One flat list of rows — restaurants first, then cuisines, then businesses — matching the
          // old event bar's typeahead order.
          <div
            aria-label={t('eventBarV2.what.resultsLabel')}
            className="flex flex-col"
            id={listboxId}
            role="listbox"
          >
            {caterers.map((result, index) => (
              <WhatTypeaheadOption
                key={result.id}
                id={resultOptionId(index)}
                isActive={activeIndex === index}
                onSelect={() => handleSelectResult(result)}
                result={result}
              />
            ))}
            {cuisines.map((result, index) => (
              <WhatTypeaheadOption
                key={result.id}
                id={resultOptionId(cuisineStart + index)}
                isActive={activeIndex === cuisineStart + index}
                onSelect={() => handleSelectResult(result)}
                result={result}
              />
            ))}
            {diversity.map((result, index) => (
              <WhatTypeaheadOption
                key={result.id}
                id={resultOptionId(diversityStart + index)}
                isActive={activeIndex === diversityStart + index}
                onSelect={() => handleSelectResult(result)}
                result={result}
              />
            ))}
          </div>
        ) : (
          <div
            aria-label={t('eventBarV2.what.resultsLabel')}
            className="flex flex-col gap-4 p-2"
            id={listboxId}
            role="listbox"
          >
            {showRecent && (
              <div>
                <div aria-hidden className="px-2 text-xs font-semibold text-peppercorn-500">
                  {t('eventBarV2.what.recentSearchesLabel')}
                </div>
                <div
                  aria-label={t('eventBarV2.what.recentSearchesLabel')}
                  className="flex flex-col"
                  role="group"
                >
                  {recentItems.map((item, index) =>
                    item.type === 'draft' ? (
                      <WhatDraftOrderOption
                        key={item.draftOrder.cartId}
                        draftOrder={item.draftOrder}
                        id={recentOptionId(index)}
                        isActive={activeIndex === index}
                        onSelect={() => handleSelectDraftOrder(item.draftOrder)}
                      />
                    ) : (
                      <EventBarSearchHistoryOption
                        key={item.entry.fulfillmentDetailId}
                        entry={item.entry}
                        id={recentOptionId(index)}
                        isActive={activeIndex === index}
                        onSelect={() => handleSelectHistory(item.entry)}
                      />
                    ),
                  )}
                </div>
              </div>
            )}
            <div>
              {showRecent && (
                <div aria-hidden className="mb-3 px-2 text-xs font-semibold text-peppercorn-500">
                  {t('eventBarV2.what.suggestedSearchesLabel')}
                </div>
              )}
              <div
                aria-label={t('eventBarV2.what.suggestedSearchesLabel')}
                className="flex flex-wrap gap-2 px-2"
                role="group"
              >
                {WHAT_SUGGESTIONS.map((suggestion, index) => (
                  <SuggestedSearchChip
                    key={suggestion.key}
                    icon={suggestion.icon}
                    id={chipOptionId(index, idSuffix)}
                    isActive={activeIndex === recentItems.length + index}
                    label={t(`eventBarV2.what.suggestions.${suggestion.key}`)}
                    onClick={() => handleSelect(suggestion)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        {whatValue.length >= MAX_CHARS && (
          <div className="px-2 pt-2">
            <InfoBanner
              data-testid="what-character-limit-alert"
              role="alert"
              text={t('eventBarV2.what.characterLimitAlert', {count: MAX_CHARS})}
              variant="warning"
            />
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

export default What;
